import { getSearchFunctionForPotentiallyEncodedStrings } from "../lib/Search/getEncodingsToSearchFor";
import { DbEventsTableReader } from '../State/EvidenceDB/DbEventsTableReader';
import { getDatabase } from "../State/EvidenceDB/getDatabase";
import { ProcessInfo } from "../State/ProcessInfo";
import { isNetworkEvent } from "../lib/Events";
import { isDomEventFiredEvent } from "../lib/Events/InstrumentationEvent";
import { executeEventually } from "../lib/utils/executeEventually";
import { DbEventSearcher, SearchOptions, SearchResultEvent } from "../lib/DbEventSearcher";
import { DbNetworkEventWithSearchResults } from "../lib/searchRecordedEvent";
import { UUID } from "../State/EvidenceDB/DatabaseTypes";

ProcessInfo.setProcess({processType: "Search Web Worker"});

export const isDbNetworkEventWithSearchResults = (c: SearchResultEvent): c is DbNetworkEventWithSearchResults =>
	"searchResults" in c;

export interface ToSearchWorkerStartMessage extends SearchOptions {
	recordingUuid: UUID;
	type: 'start';
}
export interface ToSearchWorkerUpdateMessage {
	recordingUuid: UUID;
	type: 'update';
}

export type ToSearchWorkerMessage = ToSearchWorkerStartMessage | ToSearchWorkerUpdateMessage;

export interface FromSearchWorkerNewResultsMessage {
	type: 'search-results';
	eventsFound: SearchResultEvent[];
	connectionsProcessed: number;
	eventsProcessed: number;
	ofTotalEvents: number;
	charactersProcessed: number;
}

const evidenceDb = getDatabase();

const msBetweenTransmissions = 300;

class SearchEventDbWorker extends DbEventSearcher {
	db: DbEventsTableReader;
	constructor(
		searchFunction: Awaited<ReturnType<typeof getSearchFunctionForPotentiallyEncodedStrings>>,
		options: SearchOptions & { recordingUuid: UUID},
	) {
		super(searchFunction, options);
		this.db = DbEventsTableReader.forRecordingUuid(options.recordingUuid);
		this.run();
	}


	static createSearchEventDbWorker = async (options: SearchOptions & { recordingUuid: UUID}) => {
		const [db, searchFunction] = await Promise.all([
			evidenceDb,
			getSearchFunctionForPotentiallyEncodedStrings([...options.searchTerms]),
		]);
		ProcessInfo.log("Search Worker created", options.searchTerms, options);
		if (db == null) {
			throw new Error('Evidence database not found');
		}
		return new SearchEventDbWorker(searchFunction, options);
	}


	unsentResults: SearchResultEvent[] = [];
	whenToTransmitNext: number = performance.now() + msBetweenTransmissions;

	transmitUnsentResults = (immediately?: 'immediately') => {
		if (performance.now() < this.whenToTransmitNext && immediately == null) return;
		const {eventsProcessed, charactersProcessed, connectionsProcessed} = this;
		const message: FromSearchWorkerNewResultsMessage = {
			type: 'search-results',
			eventsFound: this.unsentResults,
			eventsProcessed,
			connectionsProcessed,
			ofTotalEvents: Math.max(eventsProcessed, this.totalEvents),
			charactersProcessed,
		};
		// if (message.eventsFound.length > 0) {
		// 	ProcessInfo.log("Transmitting new search results", message);
		// }
		postMessage(message);
		this.unsentResults = [];
		this.whenToTransmitNext = performance.now() + msBetweenTransmissions;
	}

	updateTotalEvents = async () => {
		this.totalEvents = await this.db.numberOfEvents();
	}


	run = executeEventually( async () => {
		await this.updateTotalEvents();
		const startAt = (this.results[this.results.length -1]?.eventIndex ?? -1) + 1;
		await this.db.forEventsStartAtIndex(startAt, (event) => {
			this.eventsProcessed++;

			if (this.includeUserEvents && (isDomEventFiredEvent(event) || event.category === "Tabs")) {
				this.results.push(event);
				this.unsentResults.push(event);
			} else if (isNetworkEvent(event)) {

				const searchResults = this.searchContentOfEvent(event);
				if (searchResults.length > 0) {
					this.results.push({...event, searchResults});
					this.unsentResults.push({...event, searchResults});
				}
			}
			this.transmitUnsentResults();
		});
		this.transmitUnsentResults('immediately')
	});
}

let worker: SearchEventDbWorker | undefined;

let workerStarted = false;
addEventListener('message', async (event) => {
	const data = event.data as ToSearchWorkerMessage;
	if (data.type === 'start' && workerStarted === false) {
		workerStarted = true;
		worker = await SearchEventDbWorker.createSearchEventDbWorker(data);
		worker.run();
	} else if (data.type === 'update') {
		worker?.run();
	}
});